/**
 * Anvaya CRO Metrics — Site-wide Conversion Tracking
 * Tracks: add-to-cart, checkout initiation, product views, collection clicks,
 *         scroll depth, time on product page, exit intent, and button engagement
 * All data stored in localStorage under "anvaya_cro" + pushed to dataLayer for GA4
 */

(function () {
  'use strict';

  var privacyReady = false;
  var trackingAllowed = false;
  var consentResolved = false;
  var consentEpoch = 0;
  var pageStart = null;
  var sessionsStarted = new Set();

  function analyticsAllowed() {
    try {
      return privacyReady && window.Shopify.customerPrivacy.analyticsProcessingAllowed() === true;
    } catch (error) { return false; }
  }

  function sessionGet(key) {
    if (!analyticsAllowed()) return null;
    try { return sessionStorage.getItem(key); } catch (error) { return null; }
  }

  function sessionSet(key, value) {
    if (!analyticsAllowed()) return;
    try { sessionStorage.setItem(key, value); } catch (error) {}
  }

  function startSession() {
    var key = 'anvaya_session_' + Math.floor(Date.now() / 1800000);
    if (!analyticsAllowed() || sessionsStarted.has(key) || sessionGet(key)) return;
    sessionsStarted.add(key);
    sessionSet(key, '1');
    inc('sessions');
    pushEvent('cro_session_start', {
      url: window.location.pathname,
      referrer: document.referrer || 'direct',
      page_type: document.body.dataset.pageType || 'unknown',
      session_count: get('sessions') || 1
    });
  }

  function refreshConsent() {
    if (!privacyReady) return;
    var next = analyticsAllowed();
    if (next === trackingAllowed && consentResolved) return;
    consentResolved = true;
    trackingAllowed = next;
    consentEpoch++;
    pageStart = next ? Date.now() : null;
    if (next) startSession();
    else {
      // End only this integration's local analytics session. Functional cart,
      // popup and storefront preferences belong to their existing owners.
      sessionsStarted.clear();
      ['localStorage', 'sessionStorage'].forEach(function (storageName) {
        try {
          var storage = window[storageName];
          var keys = [];
          for (var i = 0; i < storage.length; i++) keys.push(storage.key(i));
          keys.forEach(function (key) {
            if (/^anvaya_cro_|^anvaya_session_/.test(key) || key === 'anvaya_begin_checkout_signature') storage.removeItem(key);
          });
        } catch (error) {}
      });
    }
  }

  // ── Helpers ──────────────────────────────────────────────────────────────
  function get(key) {
    if (!analyticsAllowed()) return null;
    try { return JSON.parse(localStorage.getItem('anvaya_cro_' + key) || 'null'); } catch (e) { return null; }
  }
  function set(key, val) {
    if (!analyticsAllowed()) return;
    try { localStorage.setItem('anvaya_cro_' + key, JSON.stringify(val)); } catch (e) {}
  }
  function inc(key) {
    set(key, (get(key) || 0) + 1);
  }

  window.dataLayer = window.dataLayer || [];

  function pushEvent(eventName, params) {
    if (!analyticsAllowed()) return;
    window.dataLayer.push({ event: eventName, ...params });

    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, params || {});
    }
  }

  function getCurrency() {
    return window.Shopify && window.Shopify.currency && window.Shopify.currency.active
      ? window.Shopify.currency.active
      : 'CAD';
  }

  function parseMoney(text) {
    if (!text) return null;

    var normalized = String(text).replace(/[^0-9.,-]/g, '').replace(/,/g, '');
    var amount = parseFloat(normalized);

    return Number.isFinite(amount) ? amount : null;
  }

  function centsToMoney(value) {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number' && Number.isFinite(value)) return value / 100;
    var amount = parseMoney(value);
    return amount !== null && amount >= 1000 ? amount / 100 : amount;
  }

  function buildShopifyItem(item) {
    if (!item) return null;

    var quantity = parseInt(item.quantity || '1', 10);
    var unitPrice = item.final_price;
    if (unitPrice == null) unitPrice = item.price;
    if (unitPrice == null && item.final_line_price != null && quantity > 0) {
      unitPrice = item.final_line_price / quantity;
    }

    var built = {
      item_id: item.variant_id || item.id || window.location.pathname,
      item_name: item.product_title || item.title || document.title.trim(),
      item_brand: item.vendor || 'Anvaya Canada',
      quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
    };

    if (item.product_type) built.item_category = item.product_type;
    if (item.variant_title && item.variant_title !== 'Default Title') built.item_variant = item.variant_title;

    unitPrice = centsToMoney(unitPrice);
    if (unitPrice !== null) built.price = unitPrice;

    return built;
  }

  function buildCartItems(cart) {
    return cart && Array.isArray(cart.items) ? cart.items.map(buildShopifyItem).filter(Boolean) : [];
  }

  function fetchCart() {
    var cartUrl = window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart';
    return fetch(cartUrl + '.js', { credentials: 'same-origin' }).then(function (response) {
      return response.ok ? response.json() : Promise.reject(new Error('cart fetch failed'));
    });
  }

  function getProductContext(source) {
    var root = source && typeof source.closest === 'function' ? source.closest('form') || document : document;
    var titleNode = document.querySelector('.product__title, h1.product__title, [itemprop="name"]');
    var priceNode = document.querySelector('.price__regular .price-item, .price .price-item--regular, [data-product-price]');
    var variantNode = root.querySelector('input[name="id"]') || document.querySelector('input[name="id"]');
    var quantityNode = root.querySelector('input[name="quantity"]') || document.querySelector('.quantity__input');
    var brandNode = document.querySelector('.product__vendor, [itemprop="brand"], [data-product-vendor]');
    var title = titleNode ? titleNode.textContent.trim() : document.title.trim();
    var price = parseMoney(priceNode ? priceNode.textContent : '');
    var quantity = parseInt(quantityNode && quantityNode.value ? quantityNode.value : '1', 10);

    return {
      item_id: variantNode && variantNode.value ? variantNode.value : window.location.pathname,
      item_name: title || 'unknown',
      item_brand: brandNode ? brandNode.textContent.replace(/^Brand:\s*/i, '').trim() : 'Anvaya Canada',
      price: price,
      quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
      currency: getCurrency(),
    };
  }

  function buildItem(context) {
    var item = {
      item_id: context.item_id,
      item_name: context.item_name,
      item_brand: context.item_brand,
      quantity: context.quantity,
    };

    if (context.price !== null) {
      item.price = context.price;
    }

    return item;
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (eventData) {
      if (!analyticsAllowed()) return;
      if (!eventData || eventData.source !== 'product-form' || !eventData.cartData) return;

      var item = buildShopifyItem(eventData.cartData);
      if (!item) return;

      pushEvent('add_to_cart', {
        currency: getCurrency(),
        value: item.price !== undefined ? item.price * item.quantity : undefined,
        items: [item]
      });
      pushEvent('cro_add_to_cart', {
        product_title: item.item_name,
        price: item.price !== undefined ? item.price : 'unknown',
        url: window.location.pathname
      });
      inc('atc_total');
    });
  }

  // ── 1. Time on Page ───────────────────────────────────────────────────────
  window.addEventListener('beforeunload', function () {
    if (!analyticsAllowed() || pageStart === null) return;
    var seconds = Math.round((Date.now() - pageStart) / 1000);
    pushEvent('cro_time_on_page', {
      page_type: document.body.dataset.pageType || 'unknown',
      seconds_spent: seconds,
      url: window.location.pathname
    });
  });

  // ── 2. Scroll Depth ───────────────────────────────────────────────────────
  // Keep threshold detection synchronous, but let the browser paint before the
  // noncritical Google event pipeline runs. This yields work; it does not remove it.
  var pendingScrollEvents = new Set();
  function deliverScrollDepth(task) {
    if (!pendingScrollEvents.has(task)) return;
    pendingScrollEvents.delete(task);
    if (task.epoch === consentEpoch) pushEvent('cro_scroll_depth', task.params);
  }
  function queueScrollDepth(depth) {
    var task = {
      epoch: consentEpoch,
      params: { depth: depth, url: window.location.pathname },
      frame: 0,
      timer: 0
    };
    pendingScrollEvents.add(task);
    task.frame = requestAnimationFrame(function () {
      task.frame = 0;
      if (!pendingScrollEvents.has(task)) return;
      task.timer = setTimeout(function () {
        deliverScrollDepth(task);
      }, 0);
    });
  }
  window.addEventListener('pagehide', function () {
    pendingScrollEvents.forEach(function (task) {
      if (task.frame) cancelAnimationFrame(task.frame);
      if (task.timer) clearTimeout(task.timer);
      // Preserve the existing delivery attempt during a fast navigation. The
      // epoch and live permission checks still reject revoked/denied activity.
      deliverScrollDepth(task);
    });
    pendingScrollEvents.clear();
  });

  var scrollMilestones = { 25: false, 50: false, 75: false, 90: false };
  window.addEventListener('scroll', function () {
    if (!analyticsAllowed()) return;
    var pct = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
    [25, 50, 75, 90].forEach(function (m) {
      if (!scrollMilestones[m] && pct >= m) {
        scrollMilestones[m] = true;
        queueScrollDepth(m);
      }
    });
  }, { passive: true });

  // ── 3. Checkout Button Click ──────────────────────────────────────────────
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[name="checkout"], .cart__checkout-button, a[href*="/checkout"]');
    if (!analyticsAllowed() || !btn || btn.disabled || btn.dataset.croCheckoutTracking === '1') return;

    btn.dataset.croCheckoutTracking = '1';
    var requestEpoch = consentEpoch;

    function requestStillAllowed() {
      return analyticsAllowed() && requestEpoch === consentEpoch;
    }

    fetchCart()
      .then(function (cart) {
        if (!requestStillAllowed()) return;
        var cartItems = buildCartItems(cart);
        var cartValue = centsToMoney(cart.total_price);
        var cartSignature = [cart.token || 'cart', cart.item_count || 0, cart.total_price || 0].join(':');

        if (sessionGet('anvaya_begin_checkout_signature') === cartSignature) return;
        sessionSet('anvaya_begin_checkout_signature', cartSignature);

        pushEvent('begin_checkout', {
          currency: cart.currency || getCurrency(),
          value: cartValue !== null ? cartValue : undefined,
          items: cartItems,
          page_location: window.location.href
        });
        pushEvent('cro_checkout_click', {
          url: window.location.pathname,
          item_count: cart.item_count || cartItems.length,
          value: cartValue !== null ? cartValue : 'unknown'
        });
        inc('checkout_clicks');
      })
      .catch(function () {
        if (!requestStillAllowed()) return;
        pushEvent('begin_checkout', {
          currency: getCurrency(),
          page_location: window.location.href
        });
        pushEvent('cro_checkout_click', { url: window.location.pathname });
        inc('checkout_clicks');
      })
      .finally(function () {
        setTimeout(function () {
          delete btn.dataset.croCheckoutTracking;
        }, 1500);
      });
  });

  // ── 4. Product Image Zoom / Gallery Interaction ───────────────────────────
  document.addEventListener('click', function (e) {
    if (e.target.closest('.product__media-item, .media-gallery__item')) {
      pushEvent('cro_image_zoom', { url: window.location.pathname });
    }
  });

  // ── 5. Variant Selection ──────────────────────────────────────────────────
  document.addEventListener('change', function (e) {
    if (e.target.matches('input[type="radio"][name], select.product-form__input')) {
      pushEvent('cro_variant_selected', {
        option_name:  e.target.name,
        option_value: e.target.value,
        url:          window.location.pathname
      });
    }
  });

  // ── 6. Exit Intent (desktop) ──────────────────────────────────────────────
  if (window.innerWidth >= 750) {
    var exitFired = false;
    document.addEventListener('mouseleave', function (e) {
      if (!analyticsAllowed()) return;
      if (!exitFired && e.clientY <= 0) {
        exitFired = true;
        pushEvent('cro_exit_intent', { url: window.location.pathname });
        inc('exit_intent');
      }
    });
  }

  // ── 7. Collection Product Card Click ─────────────────────────────────────
  document.addEventListener('click', function (e) {
    var card = e.target.closest('.card-wrapper a[href*="/products/"], .product-card a[href*="/products/"]');
    if (card) {
      var title = card.querySelector('.card__heading, .card__name, h3');
      pushEvent('select_item', {
        item_list_name: window.location.pathname,
        items: [{
          item_id: card.getAttribute('href') || card.href,
          item_name: title ? title.textContent.trim() : card.href,
          index: Array.from(document.querySelectorAll('.card-wrapper')).indexOf(card.closest('.card-wrapper')) + 1
        }]
      });
      pushEvent('cro_product_card_click', {
        product_title: title ? title.textContent.trim() : card.href,
        collection:    window.location.pathname,
        position:      Array.from(document.querySelectorAll('.card-wrapper')).indexOf(card.closest('.card-wrapper')) + 1
      });
    }
  });

  // ── 8. Search Usage ───────────────────────────────────────────────────────
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[action*="/search"]');
    if (form) {
      var q = form.querySelector('input[name="q"]');
      if (q && q.value) {
        pushEvent('search', { search_term: q.value.trim() });
        pushEvent('cro_search', { term: q.value.trim() });
        inc('search_count');
      }
    }
  });

  // ── 9. Newsletter Signup ─────────────────────────────────────────────────
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form.newsletter-form, form[action*="contact#newsletter"]');
    if (form) {
      pushEvent('sign_up', {
        method: 'newsletter',
        page_location: window.location.href
      });
      pushEvent('cro_newsletter_signup', { url: window.location.pathname });
      inc('newsletter_signups');
    }
  });

  // ── 10. Session Metrics ───────────────────────────────────────────────────
  // Start only the permitted portion of this visit; denied events are never queued.
  document.addEventListener('visitorConsentCollected', refreshConsent);
  try {
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], function (error) {
        if (error) return;
        privacyReady = true;
        refreshConsent();
      });
    }
  } catch (error) { /* No native decision means no first-party analytics. */ }

  // ── 11. Sticky Add to Cart (product page) ────────────────────────────────
  if (window.location.pathname.includes('/products/')) {
    var productForm = document.querySelector('.product-form__submit');
    if (productForm) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) {
            // ATC button scrolled out of view — product page engagement signal
            pushEvent('cro_atc_out_of_view', { url: window.location.pathname });
          }
        });
      }, { threshold: 0 });
      observer.observe(productForm);
    }
  }

  // ── 12. Report to console in dev ─────────────────────────────────────────
  if (window.location.search.includes('cro_debug=1')) {
    console.group('Anvaya CRO Metrics');
    ['atc_total', 'checkout_clicks', 'exit_intent', 'sessions', 'search_count', 'newsletter_signups'].forEach(function (k) {
      console.log(k + ':', get(k) || 0);
    });
    console.groupEnd();
  }

})();
